import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  getCookieConsent,
  setCookieConsent,
  subscribeCookieConsent,
  type ConsentValue,
} from './cookieConsentStore';

const OPEN_DELAY_MS = 1500;

export type CookieChoice = {
  decision: 'granted' | 'refused' | 'withdrawn';
  /** `settings` when the banner was reopened with `openSettings` */
  method: 'web_banner' | 'settings';
};

export type UseCookieConsentOptions = {
  /** Every Accept/Reject click, even an unchanged one (e.g. to record it on a server) */
  onChoice?: (choice: CookieChoice) => void;
  /** App-specific cleanup after a reject (e.g. clearing an analytics SDK's storage) */
  onReject?: () => void;
};

// No answer on the server, so SSR and the first client render match
const getServerConsent = (): ConsentValue => null;

export const useCookieConsent = ({
  onChoice,
  onReject,
}: UseCookieConsentOptions = {}) => {
  const consent = useSyncExternalStore(
    subscribeCookieConsent,
    getCookieConsent,
    getServerConsent,
  );
  const [isPromptDue, setIsPromptDue] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // Where the banner was opened from, reported with the choice
  const methodRef = useRef<CookieChoice['method']>('web_banner');
  // The prompt closes by itself once an answer arrives (another tab, a server restore)
  const isOpen = isSettingsOpen || (isPromptDue && consent === null);

  useEffect(() => {
    if (getCookieConsent() !== null) return;
    const timer = setTimeout(() => setIsPromptDue(true), OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const close = useCallback(() => {
    setIsPromptDue(false);
    setIsSettingsOpen(false);
  }, []);

  const reportChoice = useCallback(
    (decision: CookieChoice['decision']) => {
      onChoice?.({ decision, method: methodRef.current });
      methodRef.current = 'web_banner';
    },
    [onChoice],
  );

  const accept = useCallback(() => {
    setCookieConsent('true');
    close();
    reportChoice('granted');
  }, [close, reportChoice]);

  const reject = useCallback(() => {
    // Read before the store changes; reported before onReject clears the SDK
    const decision = getCookieConsent() === 'true' ? 'withdrawn' : 'refused';
    setCookieConsent('rejected');
    close();
    reportChoice(decision);
    onReject?.();
  }, [close, reportChoice, onReject]);

  const openSettings = useCallback(() => {
    methodRef.current = 'settings';
    setIsSettingsOpen(true);
  }, []);

  return { consent, isOpen, accept, reject, openSettings };
};
